import {
  format,
  getYear,
  isAfter,
  isBefore,
  isValid,
  parseISO,
} from "date-fns";
import { FieldValues } from "react-hook-form";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import { TalentSelfServiceOrderPermission } from "../../talents/types/queries";

export const INDEFINITE_DATE_STRING = "9999-12-31";

export const getValidityEndDate = (
  date: Date | null,
  indefinite: boolean,
): string => {
  if (indefinite) {
    return INDEFINITE_DATE_STRING;
  }
  if (date) {
    return format(date, isoFormat);
  }
  return INDEFINITE_DATE_STRING;
};

export const isIndefiniteDate = (dateString: string): boolean => {
  try {
    const date = parseISO(dateString);
    return getYear(date) >= 9999;
  } catch {
    return false;
  }
};

// Custom validation functions for DateWithIndefiniteField
export const validateEndDate = (
  value: Date | null,
  indefinite: boolean,
  permissionStart: Date | null,
) => {
  if (indefinite) {
    return true; // Valid if indefinite is checked
  }

  if (!value) {
    return "required";
  }

  if (!isValid(value)) {
    return "invalidDate";
  }

  // Check if end date is after start date
  if (
    permissionStart &&
    isValid(permissionStart) &&
    isBefore(value, permissionStart)
  ) {
    return "endDateCannotBeBeforeStartDate";
  }

  return true;
};

export const validateIndefinite = (
  value: boolean,
  fieldValues: FieldValues,
) => {
  if (value) {
    return true; // Valid if checked
  }

  const endDate = fieldValues.permissionEnd;
  if (!endDate) {
    return "required";
  }

  return true;
};

// Validation function for start date
export const validateStartDate = (value: Date | null, endDate: Date | null) => {
  if (!value) {
    return "required";
  }

  if (!isValid(value)) {
    return "invalidDate";
  }

  if (endDate && isAfter(value, endDate)) {
    return "startDateCannotBeAfterEndDate";
  }

  return true;
};

export const sortPermissions = (
  permissions: TalentSelfServiceOrderPermission[],
) => {
  return permissions.sort(
    (a, b) =>
      a.customerOrganizationName.localeCompare(
        b.customerOrganizationName,
        "fi",
      ) ||
      new Date(a.permissionStart).getTime() -
        new Date(b.permissionStart).getTime() ||
      a.workLocationName.localeCompare(b.workLocationName, "fi"),
  );
};
